import { NARRATIVE_MESSAGES_EN_V0 } from '@shared/genesis-home-capability-narrative/messages-en';
import type { RequiredTranslationKeyV0 } from './catalog';

export const EN_MESSAGES_V0: Record<RequiredTranslationKeyV0, string> = {
  ...NARRATIVE_MESSAGES_EN_V0,
  'common.appName': 'Inflomatrix',
  'common.loading': 'Loading…',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.language': 'Language',
  'common.languageDescription':
    'Choose the interface language. This is independent of currency and timezone.',

  'navigation.today': 'Today',
  'navigation.workbench': 'Workbench',
  'navigation.operations': 'Operations',
  'navigation.objects': 'Objects',
  'navigation.evidenceDecisions': 'Evidence & Decisions',
  'navigation.peopleAuthority': 'People & Authority',
  'navigation.automation': 'Automation',
  'navigation.configure': 'Configure',
  'navigation.operationsConsole': 'Operations Console',

  'states.operation.DRAFT': 'Draft',
  'states.operation.PROPOSED': 'Proposed',
  'states.operation.READY': 'Ready',
  'states.operation.ADMITTED': 'Admitted',
  'states.operation.RUNNING': 'Running',
  'states.operation.WAITING': 'Waiting',
  'states.operation.NEEDS_ATTENTION': 'Needs attention',
  'states.operation.BLOCKED': 'Blocked',
  'states.operation.DENIED': 'Denied',
  'states.operation.FAILED': 'Failed',
  'states.operation.COMPENSATING': 'Compensating',
  'states.operation.COMPLETED': 'Completed',
  'states.operation.CANCELLED': 'Cancelled',
  'states.evidence.SUBMITTED': 'Submitted',
  'states.evidence.REVIEWED': 'Reviewed',
  'states.evidence.ACCEPTED': 'Accepted',
  'states.evidence.SATISFIED': 'Satisfied',
  'states.evidence.REJECTED': 'Rejected',
  'states.authority.PENDING': 'Authority pending',
  'states.authority.GRANTED': 'Authority granted',
  'states.authority.DENIED': 'Authority denied',
  'states.sync.SCAN_OK': 'Scan succeeded',
  'states.sync.LOADING': 'Syncing',
  'states.recovery.AVAILABLE': 'Recovery available',
  'states.ai.PROPOSED': 'AI proposed',
  'states.ai.ADMITTED': 'System admitted',
  'states.task.READY': 'Task ready',
  'states.task.IN_PROGRESS': 'Task in progress',
  'states.task.DONE': 'Task done',

  'actions.review': 'Review',
  'actions.provideInformation': 'Provide Information',
  'actions.requestExecution': 'Request Execution',
  'actions.approve': 'Approve',
  'actions.deny': 'Deny',
  'actions.start': 'Start',
  'actions.resume': 'Resume',
  'actions.retry': 'Retry',
  'actions.cancel': 'Cancel',
  'actions.requestCompensation': 'Request Compensation',
  'actions.openRecovery': 'Open Recovery',
  'actions.viewReceipt': 'View Receipt',

  'errors.execution.receivingQuantityMissing.whatHappened': 'The execution did not start.',
  'errors.execution.receivingQuantityMissing.why':
    'The required receiving quantity was not provided.',
  'errors.execution.receivingQuantityMissing.whatChanged': 'Inventory was not changed.',
  'errors.execution.receivingQuantityMissing.whatDidNotChange':
    'No other operational records were changed.',
  'errors.execution.receivingQuantityMissing.nextAction':
    'Enter the quantity and request execution again.',
  'errors.shell.loadFailure.whatHappened': 'The page could not be loaded.',
  'errors.shell.loadFailure.why':
    'The response could not be interpreted or a network error occurred.',
  'errors.shell.loadFailure.whatChanged': 'On-screen data was not refreshed.',
  'errors.shell.loadFailure.whatDidNotChange':
    'Server operational data, authority, and evidence were not changed.',
  'errors.shell.loadFailure.nextAction': 'Retry or open a recovery path.',

  'today.title': 'Today',
  'today.subtitle': 'Work that needs attention now',
  'today.attention': 'Attention items',
  'workbench.title': 'Workbench',
  'workbench.subtitle': 'One governed execution',
  'workbench.stage.request': 'Request',
  'workbench.stage.intent': 'Intent',
  'workbench.stage.operation': 'Operation',
  'workbench.stage.authority': 'Authority',
  'workbench.stage.evidence': 'Evidence',
  'workbench.stage.execution': 'Execution',
  'workbench.stage.result': 'Result',
  'workbench.stage.recovery': 'Recovery',
  'genesis.title': 'Genesis',
  'genesis.stage.start': 'Start',
  'genesis.stage.sources': 'Sources',
  'genesis.stage.facts': 'Facts',
  'genesis.stage.proposals': 'Proposals',
  'genesis.stage.preview': 'Configuration preview',
  'genesis.stage.generate': 'Generate',
  'genesis.stage.firstOperation': 'First operation',
  'evidence.title': 'Evidence & Decisions',
  'evidence.needsReview': 'Needs review',
  'evidence.historyTitle': 'Evidence & decisions history',
  'evidence.historySubtitle': 'Completed review and decision records',
  'authority.title': 'Authority',
  'auth.login': 'Log in',
  'auth.logout': 'Log out',
  'settings.personalLanguage': 'Language',
  'settings.tenantDefaultLanguage': 'Default language',
  'settings.tenantDefaultLanguageHelp':
    'Applies only to users without a personal language preference. Existing personal preferences are not overwritten.',
  'public.home': 'Home',
  'public.product': 'Product',
  'public.language': 'Language',
  'public.intake.submitFailed.whatHappened': 'Submission could not be completed',
  'public.intake.submitFailed.why': 'The contact/order intake API failed or rejected the response.',
  'public.intake.submitFailed.whatChanged':
    'This screen shows an error state. A new intake may not have been created on the server.',
  'public.intake.submitFailed.whatDidNotChange':
    'Existing intakes, orders, permissions, and session were not changed by this failure.',
  'public.intake.submitFailed.nextAction': 'Review your input and try again.',
  'public.intake.waitingReason':
    'Your submission is being processed. Please wait until it completes.',
  'public.intake.waitingRequired': 'Intake API response',
  'public.intake.receipt.areaLabel': 'Public intake',
  'public.intake.receipt.whatChanged': 'The intake was accepted and queued for review.',
  'public.intake.receipt.whatDidNotChange': 'It was not executed. Accepted ≠ executed.',
  'public.receipt.orderReceived': 'Order received',
  'public.receipt.requestReceived': 'Request received',
  'public.receipt.qty': 'Qty',
  'public.receipt.total': 'Total',
  'public.receipt.submitted': 'Submitted',
  'public.receipt.acceptedNotExecuted': 'Accepted — not yet executed',
  'public.actionError.PARENT_NOT_FOUND': 'The original intake could not be found.',
  'public.actionError.PARENT_SCOPE_MISMATCH': 'You do not have access to this intake.',
  'public.actionError.ACTION_NOT_ALLOWED': 'This request is not allowed in the current state.',
  'public.actionError.DUPLICATE_ACTION': 'An identical request is already in progress.',
  'public.actionError.VALIDATION_FAILED': 'Please check your input.',
  'public.actionError.POLICY_NOT_FOUND': 'The request policy could not be found.',
  'public.actionError.fallback': 'The request failed. Please try again in a moment.',
  'public.actionError.disabled': 'This request is not available right now.',
  'public.actionError.successAccepted': 'The request was accepted. It has not been executed yet.',
  'public.actionError.failure.whatHappened': 'The follow-up request could not be submitted',
  'public.actionError.failure.whatChanged':
    'This screen shows an error. A follow-up request may not have been created.',
  'public.actionError.failure.whatDidNotChange':
    'The original intake, execution state, and permissions were not changed by this failure.',
  'public.actionError.failure.nextAction':
    'Review the reason and retry, or contact support with the reference.',
  'legal.privacy': 'Privacy',
  'legal.terms': 'Terms',
  'portal.gateway': 'Portal',
  'configure.title': 'Configure',
  'console.title': 'Operations Console',
  'operations.title': 'Operations',
  'operations.subtitle': 'Browse and manage operation types and executions',
  'objects.title': 'Objects',
  'objects.subtitle': 'Operational object context and related work',
  'people.title': 'People & Authority',
  'automation.title': 'Automation',
  'decisions.title': 'Decisions',
};
